import * as React from "react";
import AsyncSelect from "react-select/async";
import { cn } from "@/lib/utils";
import { fetchAllUsers } from "@/features/users/userService";
import { Button } from "@shared/components/ui/button";

export function MultiSelectUser({
  users = [],
  onSelect,
  className,
  placeholder = "Select users...",
  isDisabled = false,
}) {
  const [selectedUsers, setSelectedUsers] = React.useState(users || []);
  const [defaultOptions, setDefaultOptions] = React.useState([]);

  React.useEffect(() => {
    setSelectedUsers(users || []);
  }, [users]);

  // Load default options on component mount
  React.useEffect(() => {
    const loadInitialOptions = async () => {
      try {
        const queryParams = new URLSearchParams({
          brandName_q: '',
          role: "client",
          limit: 50,
          sort: '-createdAt' // Sort by creation date descending to get the latest users
        }).toString();
        
        const response = await fetchAllUsers(queryParams);
        
        if (response?.data) {
          const options = response.data.map((user) => ({
            value: user._id,
            label: user.brandName || user.fullName,
            user: user,
            ...user
          }));
          setDefaultOptions(options);
        }
      } catch (error) {
        console.error("Error loading default users:", error);
      }
    };

    loadInitialOptions();
  }, []);

  // Function to load options asynchronously
  const loadOptions = React.useCallback(async (inputValue) => {
    try {
      const queryParams = new URLSearchParams({
        brandName_q: inputValue || '',
        limit: 50,
        sort: '-createdAt' // Sort by creation date descending to get the latest users
      }).toString();
      
      const response = await fetchAllUsers(queryParams);
      
      if (response?.data) {
        return response.data.map((user) => ({
          value: user._id,
          label: user.brandName || user.fullName,
          user: user, // Keep the full user object for later use
          ...user
        }));
      }
      return [];
    } catch (error) {
      console.error("Error loading users:", error);
      return [];
    }
  }, []);

  // Custom styles for react-select to match your design
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '40px',
      border: '2px solid hsl(var(--border))',
      borderRadius: '6px',
      backgroundColor: 'hsl(var(--background))',
      boxShadow: state.isFocused ? '0 0 0 2px hsl(var(--ring))' : 'none',
      borderColor: state.isFocused ? 'hsl(var(--ring))' : 'hsl(var(--border))',
      '&:hover': {
        borderColor: 'hsl(var(--ring))',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'hsl(var(--popover))',
      border: '1px solid hsl(var(--border))',
      borderRadius: '6px',
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'hsl(var(--accent))' 
        : state.isFocused 
        ? 'hsl(var(--accent) / 0.5)' 
        : 'transparent',
      color: 'hsl(var(--foreground))',
      padding: '8px 12px',
      '&:hover': {
        backgroundColor: 'hsl(var(--accent))',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'hsl(var(--secondary))',
      borderRadius: '4px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'hsl(var(--secondary-foreground))',
      fontSize: '12px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'hsl(var(--secondary-foreground))',
      '&:hover': {
        backgroundColor: 'hsl(var(--destructive))',
        color: 'hsl(var(--destructive-foreground))',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'hsl(var(--muted-foreground))',
    }),
    input: (provided) => ({
      ...provided,
      color: 'hsl(var(--foreground))',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'hsl(var(--foreground))',
    }),
  };

  // Format option display
  const formatOptionLabel = (option) => (
    <div className="flex flex-col py-1">
      <div className="flex items-center space-x-2">
        <span className="font-medium text-foreground">
          {option.fullName || option.brandName}
        </span>
      </div>
      <div className="flex items-center space-x-2 text-xs text-muted-foreground">
        <span>{option.email}</span>
        {option.role && (
          <span className="px-2 py-1 bg-muted rounded text-xs">
            {option.role}
          </span>
        )}
      </div>
    </div>
  );

  // Handle selection change
  const handleChange = (selectedOptions) => {
    const users = selectedOptions ? selectedOptions.map(option => option.user || option) : [];
    setSelectedUsers(users);
    onSelect(users);
  };

  // Handle select all functionality
  const handleSelectAll = () => {
    const allUsers = defaultOptions.map(option => option.user || option);
    setSelectedUsers(allUsers);
    onSelect(allUsers);
  };

  // Handle clear all functionality
  const handleClearAll = () => {
    setSelectedUsers([]);
    onSelect([]);
  };

  // Convert users to options format
  const userOptions = selectedUsers.map(user => ({
    value: user._id,
    label: user.fullName || user.brandName,
    user: user,
    ...user
  }));

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          <AsyncSelect
            isMulti
            value={userOptions}
            loadOptions={loadOptions}
            defaultOptions={defaultOptions}
            onChange={handleChange}
            placeholder={placeholder}
            isDisabled={isDisabled}
            styles={customStyles}
            formatOptionLabel={formatOptionLabel}
            cacheOptions
            loadingMessage={() => "Loading users..."}
            noOptionsMessage={({ inputValue }) => 
              inputValue.length === 0 
                ? "No users available" 
                : "No users found matching your search"
            }
            className="react-select-container"
            classNamePrefix="react-select"
            isClearable={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            getOptionValue={(option) => option.value}
            menuPortalTarget={document.body}
            menuPosition="absolute"
          />
        </div>
        
        {defaultOptions.length > 0 && (
          <div className="flex gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              disabled={isDisabled || selectedUsers.length === defaultOptions.length}
              className="text-xs whitespace-nowrap"
            >
              Select All
            </Button>
            {selectedUsers.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                disabled={isDisabled}
                className="text-xs"
              >
                Clear
              </Button>
            )}
          </div>
        )}
      </div>
      
      {selectedUsers.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {selectedUsers.length} user{selectedUsers.length !== 1 ? "s" : ""} selected
            {defaultOptions.length > 0 && ` of ${defaultOptions.length}`}
          </span>
        </div>
      )}
    </div>
  );
}